import React, { useState } from "react"
import DineroFactory from "dinero.js"
import { Link } from "react-router-dom"
import * as graphql from "lib/types/generated/graphql-types"
import { Order, ProductVariant } from "lib/types/generated/graphql-types"
import { ReactComponent as ChevronDown } from "images/icons/chevron-down.svg"
import { ReactComponent as ChevronUp } from "images/icons/chevron-up.svg"
import { ReactComponent as CheckCircle } from "images/icons/circle-check.svg"

import {
  Container,
  Content,
  ExpandedSubTotalWrapper,
  ExpandedTotalWrapper,
  FallbackId,
  Footer,
  Header,
  Id,
  ImageAndDetails,
  ImageContainer,
  Name,
  OrderLineTotal,
  ParentId,
  ProductImage,
  VariantId,
  VatWrapper
} from "./OrderLine.styled"
import Money from "../../Money"
import { handleImageError } from "helpers/image"
import OrderLineOptions from "./OrderLineOptions"
import OrderLineOperations from "./OrderLineOperations"
import { Compensation } from "../../../classes/order/Compensation"
import CopyButton from "../../Ui/CopyButton"
import GET_PRODUCT_VARIANT from "../../../graphql/queries/product/GetProductVariant"
import { useQuery } from "@apollo/client"
import ShowVariant from "../../../views/Products/ShowVariant"
import { hideEditSidebar, showEditSidebar } from "lib/store/services/editSidebar/slice"
import { useAppDispatch } from "lib/store"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import CampaignsAndDiscounts from "./CampaignsAndDiscounts/CampaignsAndDiscounts"
import Operations from "./Operations/Operations"
import { Refund } from "../../../classes/order/Refund"
import { Release } from "../../../classes/order/Release"
import { Cancellation } from "../../../classes/order/Cancellation"

type Props = {
  orderLine: graphql.OrderLine
  orderHistory: graphql.OrderHistory
  upsell: boolean
  order: Order
}

export const Orderline = ({ orderLine, orderHistory, upsell, order }: Props) => {
  const [showFullSummary, setShowFullSummary] = useState<boolean>(false)
  const [selectedVariant, setSelectedVariant] = React.useState<ProductVariant>()
  const compensation = new Compensation(order.Compensations)
  const refund = new Refund(order.Refunds)
  const release = new Release(order.Releases)
  const cancellations = new Cancellation(order.Cancellations)

  const orderLineRefundedAmount = refund.orderLineRefundedAmount(orderLine.id)
  const releasedOrderLine = release.orderLineReleased(orderLine.id)
  const cancelledOrder = cancellations.orderCancelled(order.id)

  const releasedAmount =
    releasedOrderLine?.orderLines?.find((ol) => ol.orderLineId === orderLine.id)?.totalAmount ?? 0

  const dispatch = useAppDispatch()

  const { data, loading } = useQuery(GET_PRODUCT_VARIANT, {
    variables: { id: orderLine.productVariantId },
    fetchPolicy: "cache-first",
    context: {
      skipRumLogging: true
    }
  })
  const variant = data?.Product?.variant

  if (!orderLine) return null

  const compensationTotal = compensation.orderLineTotal(orderLine.id)

  const handleVariantClick = () => {
    if (variant) {
      setSelectedVariant(variant)
      dispatch(showEditSidebar())
    }
  }

  const handleClose = () => {
    setSelectedVariant(undefined)
    dispatch(hideEditSidebar())
  }

  const getCustomerPaid = () => {
    if (cancelledOrder) {
      return 0
    }
    return (
      orderLine.distributedTotalPriceAmount -
      orderLineRefundedAmount -
      compensationTotal -
      releasedAmount
    )
  }

  const showCustomerHasPaid = order.isTaxIncludedInPrice || order.Compensations.length === 0

  return (
    <Container>
      <Header onClick={() => setShowFullSummary(!showFullSummary)}>
        {showFullSummary ? <ChevronUp /> : <ChevronDown />}
      </Header>
      {!!selectedVariant && <ShowVariant variant={variant} cancelEvent={handleClose} />}
      <Content $showFullSummary={showFullSummary} $showCustomerHasPaid={showCustomerHasPaid}>
        <ImageAndDetails>
          <ImageContainer
            $showFullSummary={showFullSummary}
            onClick={handleVariantClick}
            $loading={loading}
          >
            <ProductImage
              $showFullSummary={showFullSummary}
              src={orderLine.imageUrl as string}
              onError={handleImageError}
            />
          </ImageContainer>
          <div>
            <Name onClick={handleVariantClick} $loading={loading}>
              {orderLine.displayName}{" "}
            </Name>
            <Id>
              <WithAuth
                requiredGroups={PermissionGroups.products.read}
                fallback={
                  <FallbackId>
                    <b>Variant Id: </b>
                    <div onClick={handleVariantClick}>{orderLine.productVariantId}</div>
                    <CopyButton string={orderLine.productVariantId} />
                  </FallbackId>
                }
              >
                <VariantId>
                  <b>Variant Id: </b>
                  <div onClick={handleVariantClick}>{orderLine.productVariantId}</div>
                  <CopyButton string={orderLine.productVariantId} />
                </VariantId>
              </WithAuth>
              <WithAuth
                requiredGroups={PermissionGroups.products.read}
                fallback={
                  <FallbackId>
                    <b>Parent Id:</b> <div>{orderLine.productParentId}</div>
                    <CopyButton string={orderLine.productParentId} />
                  </FallbackId>
                }
              >
                <ParentId>
                  <b>Parent Id:</b>{" "}
                  <Link to={`/products/${orderLine.productParentId}`}>
                    {orderLine.productParentId}
                  </Link>
                  <CopyButton string={orderLine.productParentId} />
                </ParentId>
              </WithAuth>
            </Id>
          </div>
        </ImageAndDetails>
        {!showFullSummary ? (
          <>
            <OrderLineTotal>
              Order line total{" "}
              <b>
                <Money
                  amount={orderLine.salePriceAmount * orderLine.quantity}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              </b>
            </OrderLineTotal>
            {showCustomerHasPaid && <OrderLineTotal>
              Customer has paid{" "}
              <b>
                <CheckCircle />
                <Money
                  amount={getCustomerPaid()}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              </b>
            </OrderLineTotal>}
          </>
        ) : (
          <div>
            <ExpandedTotalWrapper>
              <div>Variant base price</div>
              <Money
                amount={orderLine.basePriceAmount}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </ExpandedTotalWrapper>
            {orderLine.salePriceAmount !== orderLine.basePriceAmount && (
              <ExpandedTotalWrapper>
                <div>Variant sale price</div>
                <Money
                  amount={orderLine.salePriceAmount}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              </ExpandedTotalWrapper>
            )}
            <ExpandedTotalWrapper>
              <div>Quantity</div>
              <div>{orderLine.quantity}</div>
            </ExpandedTotalWrapper>
            <ExpandedSubTotalWrapper>
              <div>Subtotal</div>
              <Money
                amount={orderLine.salePriceAmount * orderLine.quantity}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </ExpandedSubTotalWrapper>
          </div>
        )}
      </Content>
      {showFullSummary && (
        <CampaignsAndDiscounts orderLine={orderLine} currencyUnit={order.currencyCode} />
      )}
      {showFullSummary && (
        <Operations
          order={order}
          orderLineId={orderLine.id}
          salePriceAmount={orderLine.salePriceAmount * orderLine.quantity}
        />
      )}
      {showFullSummary && (
        <>
          <VatWrapper>
            <span>
              VAT{" "}
              {(orderLine.taxPercentage || 0) / Math.pow(10, orderLine.taxPercentageDecimals || 0)}%
            </span>
            <Money
              amount={orderLine.totalTaxAmount}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </VatWrapper>
          <OrderLineTotal>
            Order line total{" "}
            <b>
              <Money
                amount={orderLine.salePriceAmount * orderLine.quantity}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </b>
          </OrderLineTotal>
          {showCustomerHasPaid && <OrderLineTotal>
            Customer has paid{" "}
            <b>
              <CheckCircle />
              <Money
                amount={getCustomerPaid()}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </b>
          </OrderLineTotal>}
        </>
      )}
      <Footer>
        <OrderLineOperations
          orderLine={orderLine}
          compensationTotal={compensationTotal}
          orderHistory={orderHistory}
          order={order}
          upsell={upsell}
        />
      </Footer>
      {showFullSummary && orderLine.options.length > 0 && (
        <OrderLineOptions options={orderLine.options} />
      )}
    </Container>
  )
}
